import React, { useEffect, useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import axios from "axios";
import { SERVICE_LOCATION, GET_COMPLETE_WAGES } from "../constants/endpoints";

import "./../assets/styles/table.css";
import "./../assets/styles/worksheets.css";

const CompleteWages = (props) => {
    const toast = useRef(null);

    const { data } = props;
    const userDepot = (data !== undefined && data !== null) ? data.data.userDetails.depotCode : "";

    const [completeWages, setCompleteWages] = useState(null);
    const [filteredReportData, setFilteredReportData] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [showCompleteWages, setShowCompleteWages] = useState(null);
    const [loading, setLoading] = useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = useState(true);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    useEffect(() => {
        initFilters();
    }, []);

    const getCompleteWages = () => {
        setLoading(true);
        const startDate = getFormattedDate(selectedStartDate);
        const endDate = getFormattedDate(selectedEndDate);
        const requestedData = {
            StartDate: startDate,
            EndDate: endDate,
            Depot: userDepot
        }
        const endPoint = SERVICE_LOCATION + GET_COMPLETE_WAGES;

        axios.post(endPoint, requestedData, {
            headers: {
                Authorization: `Bearer ${data.data.token}`,
            },
        })
            .then((response) => {
                setLoading(false);
                if (response.data.isSuccess) {
                    const processedData = response.data.data.completeWages;
                    setCompleteWages(processedData);
                }
                else if (!response.data.isSuccess) {
                    setCompleteWages(null);
                    toast.current.show({ severity: "error", summary: "Error", detail: response.data.errorMessage, life: 3000 });
                }
            })
            .catch((error) => {
                toast.current.show({ severity: "error", summary: "Error", detail: error, life: 3000 });
            })
    }

    const getFormattedDate = (dateToFormat) => {
        const year = dateToFormat.getFullYear();
        const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
        const day = String(dateToFormat.getDate()).padStart(2, "0");
        const formatedDate = `${year}-${month}-${day}`;
        return formatedDate;
    }

    const handleStartDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedStartDate(dateSelected);
        setMinDate(dateSelected);
        setSelectedEndDate(null);
        setDisableSubmitButton(true);
    }

    const handleEndDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedEndDate(dateSelected);
        setDisableSubmitButton(false);
    }

    const handleCompleteWages = () => {
        getCompleteWages();
        setShowCompleteWages(true);
    }

    const getFilteredReportData = (filteredData) => {
        setFilteredReportData(filteredData);
    }

    //export functionality
    const saveAsExcelFile = (buffer, fileName) => {
        import("file-saver").then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
                let EXCEL_EXTENSION = ".xlsx";
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportExcel = () => {
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(filteredReportData);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            saveAsExcelFile(excelBuffer, "MaterialManufacturerReport");
        });
    };
    //end export functionality

    // Search functionality
    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            module: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fitterId: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fitterName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            jobDepot: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fitterDepot: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measure: { value: null, matchMode: FilterMatchMode.CONTAINS },
            day: { value: null, matchMode: FilterMatchMode.CONTAINS },
            date: { value: null, matchMode: FilterMatchMode.CONTAINS },
            wage: { value: null, matchMode: FilterMatchMode.CONTAINS },
            proRataPoints: { value: null, matchMode: FilterMatchMode.CONTAINS },
            proRataRevenue: { value: null, matchMode: FilterMatchMode.CONTAINS },
            proRataM2: { value: null, matchMode: FilterMatchMode.CONTAINS },
            percentageFull: { value: null, matchMode: FilterMatchMode.CONTAINS },
            plotAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
            locationPostcode: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
            site: { value: null, matchMode: FilterMatchMode.CONTAINS },
            addedBy: { value: null, matchMode: FilterMatchMode.CONTAINS },
            jobDropped: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
        setGlobalFilterValue("");
    }

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const renderHeader = () => {
        return (
            <div className="row">
                <div className="col-12 col-sm-3 col-md-3 col-lg-3 col-xl-3">
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button className="mar-r-10" type="button" icon="pi pi-file-excel" severity="success" onClick={exportExcel} rounded tooltip="Export Report" />
                    </div>
                </div>
                <div className="col-12 col-sm-9 col-md-9 col-lg-9 col-xl-9">
                    <div className="flex justify-content-end txt-right">
                        <Button className="mar-r-10" type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} tooltip="clear filters" tooltipOptions={{ position: "bottom" }} />
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const header = renderHeader();
    // Search functionality Ends here

    return (
        <div className={`app ${loading ? "disabled" : ""}`}>
            {
                loading &&
                <div className="spinner-overlay">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            }
            <Toast ref={toast} />

            <div className="worksheets-section padl-20">
                <div className="row">
                    <h4>Complete Wages</h4>
                </div>
            </div>
            <div className="worksheets-section padl-20">
                <div className="row pad-tb-10">
                    <div className="col-3">
                        <label htmlFor="start-date">Start Date</label>
                        <Calendar id="start-date" name="start-date" value={selectedStartDate} onChange={handleStartDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <label htmlFor="end-date">End Date</label>
                        <Calendar id="end-date" name="end-date" value={selectedEndDate} minDate={minDate} onChange={handleEndDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <Button id="submit" label="Submit" disabled={disableSubmitButton} onClick={handleCompleteWages} />
                    </div>
                </div>
            </div>

            <div className="worksheets-section">
                {
                    showCompleteWages &&
                    <div className="row">
                        <div className="col-12">
                            <DataTable className="font-12" value={completeWages} dataKey="wageId" paginator rows={10}
                                rowsPerPageOptions={[10, 25, 50]} filters={filters} header={header} filterDisplay="row" onValueChange={getFilteredReportData}
                                globalFilterFields={["wageId", "measureId", "module", "fitterId", "fitterName", "jobDepot", "fitterDepot",
                                    "measure", "day", "date", "wage", "proRataPoints", "proRataRevenue", "proRataM2", "percentageFull",
                                    "plotAddress", "siteRef", "site", "addedBy", "jobDropped"]}
                                scrollable tableStyle={{ minWidth: "100rem" }} emptyMessage="No complete availabe for the selected date range and depot.">
                                <Column field="wageId" key="wageId" header="Wage Id" className="table-header" hidden></Column>
                                <Column field="measureId" header="Measure Id" className="table-header" hidden></Column>
                                <Column field="module" header="Module" filter filterPlaceholder="Search by module" className="table-header" ></Column>
                                <Column field="fitterId" header="Fitter Id" filter filterPlaceholder="Search by fitter id" className="table-header" ></Column>
                                <Column field="fitterName" header="Fitter Name" filter filterPlaceholder="Search by fitter name" className="table-header"></Column>
                                <Column field="jobDepot" header="Job Depot" filter filterPlaceholder="Search by job depot" className="table-header"></Column>
                                <Column field="fitterDepot" header="Fitter Depot" filter filterPlaceholder="Search by fitter depot" className="table-header"></Column>
                                <Column field="measure" header="Measure" filter filterPlaceholder="Search by measure" className="table-header"></Column>
                                <Column field="day" header="Day" filter filterPlaceholder="Search by day" className="table-header"></Column>
                                <Column field="date" header="Date" filter filterPlaceholder="Search by date" className="table-header"></Column>
                                <Column field="wage" header="Wage" filter filterPlaceholder="Search by wage" className="table-header"></Column>
                                <Column field="proRataPoints" header="Pro Rata Points" filter filterPlaceholder="Search by pro rata points" className="table-header"></Column>
                                <Column field="proRataRevenue" header="Pro Rata Revenue" filter filterPlaceholder="Search by pro rata revenue" className="table-header"></Column>
                                <Column field="proRataM2" header="Pro Rata M2" filter filterPlaceholder="Search by pro rata m2" className="table-header"></Column>
                                <Column field="percentageFull" header="% Full" filter filterPlaceholder="Search by percentage" className="table-header"></Column>
                                <Column field="plotAddress" header="Plot/Address" filter filterPlaceholder="Search by plot address" className="table-header"></Column>
                                <Column field="locationPostcode" header="Location/Postcode" filter filterPlaceholder="Search by location" className="table-header"></Column>
                                <Column field="siteRef" header="Site Ref/Job Id" filter filterPlaceholder="Search by site ref" className="table-header"></Column>
                                <Column field="site" header="Site" filter filterPlaceholder="Search by site" className="table-header"></Column>
                                <Column field="addedBy" header="Added By" filter filterPlaceholder="Search by added by" className="table-header"></Column>
                                <Column field="jobDropped" header="Job Dropped" filter filterPlaceholder="Search by job dropped" className="table-header"></Column>
                            </DataTable>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default CompleteWages;